import React, { useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import TranscriptBlock from './TranscriptBlock';
import BlockInsertZone from './BlockInsertZone';

/**
 * TranscriptBlocks Container Component
 * 
 * Container for all transcript blocks with virtualization support
 */
const TranscriptBlocks = ({
  blocks = [],
  speakers = [],
  currentBlockId,
  focusedBlock,
  selectedBlocks = [],
  onTextChange,
  onSpeakerChange,
  onFocusBlock,
  onBlurBlock,
  onSelectBlock,
  onSplit,
  onMergeUp,
  onMergeDown,
  onInsertBlock,
  onDelete,
  onDuplicate,
  onSeekToBlock,
  onSeekTo,
  onPlay,
  currentTime,
  isPlaying = false,
  readOnly = false,
  className = '',
  scrollParentRef = null,
}) => {
  // Safety check: ensure blocks is always an array
  const safeBlocks = Array.isArray(blocks) ? blocks : [];

  // Enable virtualization only for large transcripts (safe fallback)
  const shouldVirtualize = safeBlocks.length >= 200;

  const isSelectedSet = useMemo(() => new Set(selectedBlocks), [selectedBlocks]);
  
  return (
    <div
      id="transcript-blocks-container"
      className={`te-transcript-area space-y-3 ${className}`}
      style={{ scrollMargin: 0, scrollPadding: 0 }}
    >
      {safeBlocks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 16px', color: '#71717a' }}>
          <p style={{ fontSize: '14px', fontWeight: 500, margin: 0 }}>No transcript available</p>
          <p style={{ fontSize: '12.5px', marginTop: '6px', color: '#a1a1aa' }}>Generate a transcript to start editing</p>
        </div>
      ) : !shouldVirtualize ? (
        safeBlocks.map((block, index) => (
          <React.Fragment key={block.id}>
            <TranscriptBlock
              block={block}
              speakers={speakers}
              isActive={currentBlockId === block.id}
              isFocused={focusedBlock === block.id}
              isSelected={isSelectedSet.has(block.id)}
              onTextChange={onTextChange}
              onSpeakerChange={onSpeakerChange}
              onFocus={onFocusBlock}
              onBlur={onBlurBlock}
              onSelect={onSelectBlock}
              onSplit={onSplit}
              onMergeUp={onMergeUp}
              onMergeDown={onMergeDown}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onSeekToBlock={onSeekToBlock}
              onSeekTo={onSeekTo}
              onPlay={onPlay}
              currentTime={currentBlockId === block.id ? currentTime : null}
              isPlaying={isPlaying}
              readOnly={readOnly}
            />
            {/* Insert zone between blocks */}
            {index < safeBlocks.length - 1 && (
              <BlockInsertZone
                afterBlockId={block.id}
                onInsert={onInsertBlock}
                readOnly={readOnly}
              />
            )}
          </React.Fragment>
        ))
      ) : (
        <Virtuoso
          customScrollParent={scrollParentRef?.current || undefined}
          data={safeBlocks}
          // Ensure stable scroll behavior; Virtuoso handles variable heights
          itemContent={(index, block) => {
            const isActive = currentBlockId === block.id;
            const isFocused = focusedBlock === block.id;
            return (
              <div className="space-y-4">
                <TranscriptBlock
                  block={block}
                  speakers={speakers}
                  isActive={isActive}
                  isFocused={isFocused}
                  isSelected={isSelectedSet.has(block.id)}
                  onTextChange={onTextChange}
                  onSpeakerChange={onSpeakerChange}
                  onFocus={onFocusBlock}
                  onBlur={onBlurBlock}
                  onSelect={onSelectBlock}
                  onSplit={onSplit}
                  onMergeUp={onMergeUp}
                  onMergeDown={onMergeDown}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onSeekToBlock={onSeekToBlock}
                  onSeekTo={onSeekTo}
                  onPlay={onPlay}
                  currentTime={isActive ? currentTime : null}
                  isPlaying={isPlaying}
                  readOnly={readOnly}
                />
                {index < safeBlocks.length - 1 && (
                  <BlockInsertZone
                    afterBlockId={block.id}
                    onInsert={onInsertBlock}
                    readOnly={readOnly}
                  />
                )}
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

export default TranscriptBlocks;


